import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp, X, SlidersHorizontal } from "lucide-react";

export interface FilterValues {
  priceRange: { min: number | undefined; max: number | undefined };
  brands: string[];
  productTypes: string[];
  rating: number | null;
  inStock: boolean;
  onSale: boolean;
  freeShipping: boolean;
}

interface ProductFilterPanelProps {
  filters: FilterValues;
  onFilterChange: (filters: FilterValues) => void;
  availableBrands?: string[];
  availableProductTypes?: string[];
  priceMin?: number;
  priceMax?: number;
  onClearAll?: () => void;
  isMobile?: boolean;
  onClose?: () => void;
}

// ─── Price Range Slider ───────────────────────────────────────────────────────

interface PriceSliderProps {
  min: number;
  max: number;
  valueMin: number;
  valueMax: number;
  onChange: (min: number, max: number) => void;
  onCommit: (min: number, max: number) => void;
}

const PriceSlider: React.FC<PriceSliderProps> = ({
  min,
  max,
  valueMin,
  valueMax,
  onChange,
  onCommit,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);

  // Keep latest values in refs so pointer handlers never close over stale state
  const valueMinRef = useRef(valueMin);
  const valueMaxRef = useRef(valueMax);
  useEffect(() => { valueMinRef.current = valueMin; }, [valueMin]);
  useEffect(() => { valueMaxRef.current = valueMax; }, [valueMax]);

  const clamp = (v: number) => Math.max(min, Math.min(max, v));

  const posToValue = (clientX: number): number => {
    const rect = trackRef.current!.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    return clamp(Math.round(ratio * (max - min) + min));
  };

  const toPercent = (v: number) =>
    max === min ? 0 : ((v - min) / (max - min)) * 100;

  // ── pointer capture approach — thumb keeps pointer even if cursor leaves ──

  const startDrag = (_thumb: "min" | "max") => (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Lock pointer to this element — no more losing focus on fast moves
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (thumb: "min" | "max") => (e: React.PointerEvent<HTMLDivElement>) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    const v = posToValue(e.clientX);
    if (thumb === "min") {
      onChange(Math.min(v, valueMaxRef.current - 1), valueMaxRef.current);
    } else {
      onChange(valueMinRef.current, Math.max(v, valueMinRef.current + 1));
    }
  };

  const onPointerUp = (thumb: "min" | "max") => (e: React.PointerEvent<HTMLDivElement>) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (thumb === "min") {
      onCommit(valueMinRef.current, valueMaxRef.current);
    } else {
      onCommit(valueMinRef.current, valueMaxRef.current);
    }
  };

  // ── keyboard support ──────────────────────────────────────────────────────

  const onKeyMin = (e: React.KeyboardEvent) => {
    let v = valueMin;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") v = Math.min(valueMin + 1, valueMax - 1);
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") v = Math.max(valueMin - 1, min);
    else return;
    e.preventDefault();
    onChange(v, valueMax);
    onCommit(v, valueMax);
  };

  const onKeyMax = (e: React.KeyboardEvent) => {
    let v = valueMax;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") v = Math.min(valueMax + 1, max);
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") v = Math.max(valueMax - 1, valueMin + 1);
    else return;
    e.preventDefault();
    onChange(valueMin, v);
    onCommit(valueMin, v);
  };

  const leftPct = toPercent(valueMin);
  const rightPct = toPercent(valueMax);

  return (
    <div className="px-2">
      {/* Track */}
      <div
        ref={trackRef}
        className="relative h-1.5 w-full rounded-full bg-section-border my-3"
      >
        {/* Active range */}
        <div
          className="absolute h-full rounded-full bg-primary"
          style={{ left: `${leftPct}%`, right: `${100 - rightPct}%` }}
        />

        {/* Min thumb */}
        <div
          role="slider"
          aria-label="Minimum price"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={valueMin}
          tabIndex={0}
          onPointerDown={startDrag("min")}
          onPointerMove={onPointerMove("min")}
          onPointerUp={onPointerUp("min")}
          onKeyDown={onKeyMin}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 size-4 rounded-full border-2 border-primary bg-white shadow-sm
            hover:ring-4 hover:ring-primary/20 focus-visible:ring-4 focus-visible:ring-primary/30 focus-visible:outline-none
            transition-shadow cursor-grab active:cursor-grabbing"
          style={{ left: `${leftPct}%`, zIndex: leftPct > 90 ? 3 : 2 }}
        />

        {/* Max thumb */}
        <div
          role="slider"
          aria-label="Maximum price"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={valueMax}
          tabIndex={0}
          onPointerDown={startDrag("max")}
          onPointerMove={onPointerMove("max")}
          onPointerUp={onPointerUp("max")}
          onKeyDown={onKeyMax}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 size-4 rounded-full border-2 border-primary bg-white shadow-sm
            hover:ring-4 hover:ring-primary/20 focus-visible:ring-4 focus-visible:ring-primary/30 focus-visible:outline-none
            transition-shadow cursor-grab active:cursor-grabbing"
          style={{ left: `${rightPct}%`, zIndex: 3 }}
        />
      </div>

      {/* Labels */}
      <div className="flex justify-between text-xs text-section-muted">
        <span>${valueMin}</span>
        <span>${valueMax}</span>
      </div>
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

const ProductFilterPanel: React.FC<ProductFilterPanelProps> = ({
  filters,
  onFilterChange,
  availableBrands = [],
  availableProductTypes = [],
  priceMin = 0,
  priceMax = 1000,
  onClearAll,
  isMobile = false,
  onClose,
}) => {
  const SLIDER_MIN = 0;
  const sliderMax = priceMax > 0 ? priceMax : 1000;

  const [filterMin, setFilterMin] = useState(filters.priceRange.min ?? priceMin);
  const [filterMax, setFilterMax] = useState(filters.priceRange.max ?? priceMax);

  const [expandedSections, setExpandedSections] = useState({
    price: true,
    brand: true,
    productType: true,
    rating: true,
    availability: true,
  });

  // Sync when parent filter or price bounds change
  useEffect(() => {
    setFilterMin(filters.priceRange.min ?? priceMin);
    setFilterMax(filters.priceRange.max ?? priceMax);
  }, [filters.priceRange.min, filters.priceRange.max, priceMin, priceMax]);

  const clamp = (v: number, lo: number, hi: number) =>
    Math.max(lo, Math.min(hi, v));

  const commitFilter = (newMin: number, newMax: number) => {
    const safeMin = clamp(newMin, SLIDER_MIN, newMax - 1);
    const safeMax = clamp(newMax, safeMin + 1, sliderMax);
    setFilterMin(safeMin);
    setFilterMax(safeMax);
    onFilterChange({ ...filters, priceRange: { min: safeMin, max: safeMax } });
  };

  const toggleSection = (section: keyof typeof expandedSections) =>
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const handleBrandToggle = (brand: string) => {
    const newBrands = filters.brands.includes(brand)
      ? filters.brands.filter((b) => b !== brand)
      : [...filters.brands, brand];
    onFilterChange({ ...filters, brands: newBrands });
  };

  const handleProductTypeToggle = (type: string) => {
    const newTypes = filters.productTypes.includes(type)
      ? filters.productTypes.filter((t) => t !== type)
      : [...filters.productTypes, type];
    onFilterChange({ ...filters, productTypes: newTypes });
  };

  const handleAvailabilityToggle = (key: "inStock" | "onSale" | "freeShipping") =>
    onFilterChange({ ...filters, [key]: !filters[key] });

  const getActiveFilterCount = () => {
    let count = 0;
    if (
      (filters.priceRange.min !== undefined && filters.priceRange.min !== priceMin) ||
      (filters.priceRange.max !== undefined && filters.priceRange.max !== priceMax)
    )
      count++;
    if (filters.brands.length > 0) count += filters.brands.length;
    if (filters.productTypes.length > 0) count += filters.productTypes.length;
    if (filters.rating !== null) count++;
    if (filters.inStock) count++;
    if (filters.onSale) count++;
    if (filters.freeShipping) count++;
    return count;
  };

  const activeCount = getActiveFilterCount();

  return (
    <div
      className={`bg-section-card-bg ${isMobile ? "h-full" : "rounded-xl shadow-sm border border-section-border"} overflow-hidden flex flex-col`}
    >
      {/* Header */}
      <div className="bg-primary px-6 py-4 border-b border-primary">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SlidersHorizontal className="w-5 h-5 text-primary-foreground" />
            <h2 className="text-lg font-bold text-primary-foreground">Filters</h2>
            {activeCount > 0 && (
              <span className="bg-accent/20 text-accent border border-accent/40 text-xs font-bold px-2 py-0.5 rounded-full">
                {activeCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeCount > 0 && (
              <button
                onClick={() => onClearAll?.()}
                className="text-xs text-primary-foreground/60 hover:text-primary-foreground font-semibold underline transition-colors"
              >
                Clear All
              </button>
            )}
            {isMobile && onClose && (
              <button
                onClick={onClose}
                className="text-primary-foreground/60 hover:text-primary-foreground p-1 transition-colors"
                aria-label="Close filters"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Sections */}
      <div className={`overflow-y-auto flex-1 ${isMobile ? "px-4" : "px-6"} py-4 space-y-4`}>

        {/* Price Range */}
        <div className="border-b border-section-border pb-4">
          <button
            onClick={() => toggleSection("price")}
            className="w-full flex items-center justify-between py-2 hover:text-accent transition-colors"
          >
            <span className="font-semibold text-section-text">Price Range</span>
            {expandedSections.price ? (
              <ChevronUp className="w-5 h-5 text-section-muted" />
            ) : (
              <ChevronDown className="w-5 h-5 text-section-muted" />
            )}
          </button>

          {expandedSections.price && (
            <div className="mt-4 space-y-2">
              {/* Number inputs */}
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-section-muted mb-1">Min</label>
                  <input
                    type="number"
                    min={SLIDER_MIN}
                    max={filterMax - 1}
                    value={filterMin}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (!isNaN(v)) setFilterMin(v);
                    }}
                    onBlur={() => commitFilter(filterMin, filterMax)}
                    className="w-full px-3 py-2 bg-section-surface border border-section-border text-section-text rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/40"
                  />
                </div>
                <span className="text-section-muted mt-5">–</span>
                <div className="flex-1">
                  <label className="block text-xs text-section-muted mb-1">Max</label>
                  <input
                    type="number"
                    min={filterMin + 1}
                    max={sliderMax}
                    value={filterMax}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (!isNaN(v)) setFilterMax(v);
                    }}
                    onBlur={() => commitFilter(filterMin, filterMax)}
                    className="w-full px-3 py-2 bg-section-surface border border-section-border text-section-text rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/40"
                  />
                </div>
              </div>

              {/* Slider */}
              <PriceSlider
                min={SLIDER_MIN}
                max={sliderMax}
                valueMin={filterMin}
                valueMax={filterMax}
                onChange={(lo, hi) => {
                  setFilterMin(lo);
                  setFilterMax(hi);
                }}
                onCommit={commitFilter}
              />
            </div>
          )}
        </div>

        {/* Brand */}
        {availableBrands.length > 0 && (
          <div className="border-b border-section-border pb-4">
            <button
              onClick={() => toggleSection("brand")}
              className="w-full flex items-center justify-between py-2 hover:text-accent transition-colors"
            >
              <span className="font-semibold text-section-text">Brand</span>
              {expandedSections.brand ? (
                <ChevronUp className="w-5 h-5 text-section-muted" />
              ) : (
                <ChevronDown className="w-5 h-5 text-section-muted" />
              )}
            </button>
            {expandedSections.brand && (
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                {availableBrands.map((brand) => (
                  <label
                    key={brand}
                    className="flex items-center gap-3 cursor-pointer hover:bg-section-surface p-2 rounded-lg transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={filters.brands.includes(brand)}
                      onChange={() => handleBrandToggle(brand)}
                      className="w-4 h-4 accent-primary cursor-pointer"
                    />
                    <span className="text-sm text-section-text">{brand}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Product Type */}
        {availableProductTypes.length > 0 && (
          <div className="border-b border-section-border pb-4">
            <button
              onClick={() => toggleSection("productType")}
              className="w-full flex items-center justify-between py-2 hover:text-accent transition-colors"
            >
              <span className="font-semibold text-section-text">Product Type</span>
              {expandedSections.productType ? (
                <ChevronUp className="w-5 h-5 text-section-muted" />
              ) : (
                <ChevronDown className="w-5 h-5 text-section-muted" />
              )}
            </button>
            {expandedSections.productType && (
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                {availableProductTypes.map((type) => (
                  <label
                    key={type}
                    className="flex items-center gap-3 cursor-pointer hover:bg-section-surface p-2 rounded-lg transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={filters.productTypes.includes(type)}
                      onChange={() => handleProductTypeToggle(type)}
                      className="w-4 h-4 accent-primary cursor-pointer"
                    />
                    <span className="text-sm text-section-text">{type}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Rating (placeholder) */}
        <div className="border-b border-section-border pb-4">
          <button
            onClick={() => toggleSection("rating")}
            className="w-full flex items-center justify-between py-2 hover:text-accent transition-colors"
          >
            <span className="font-semibold text-section-text">Rating</span>
            {expandedSections.rating ? (
              <ChevronUp className="w-5 h-5 text-section-muted" />
            ) : (
              <ChevronDown className="w-5 h-5 text-section-muted" />
            )}
          </button>
          {expandedSections.rating && (
            <div className="mt-3 flex items-center justify-center">
              <span className="inline-flex items-center gap-2 text-section-muted text-sm italic px-3 py-1 rounded border border-dashed border-section-border bg-section-surface">
                <svg className="w-4 h-4 text-neutral-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l2 2m8-2a10 10 0 11-20 0 10 10 0 0120 0z" />
                </svg>
                Rating filter coming soon
              </span>
            </div>
          )}
        </div>

        {/* Availability */}
        <div className="pb-4">
          <button
            onClick={() => toggleSection("availability")}
            className="w-full flex items-center justify-between py-2 hover:text-accent transition-colors"
          >
            <span className="font-semibold text-section-text">Availability</span>
            {expandedSections.availability ? (
              <ChevronUp className="w-5 h-5 text-section-muted" />
            ) : (
              <ChevronDown className="w-5 h-5 text-section-muted" />
            )}
          </button>
          {expandedSections.availability && (
            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-3 cursor-pointer hover:bg-section-surface p-2 rounded-lg transition-colors">
                <input
                  type="checkbox"
                  checked={filters.onSale}
                  onChange={() => handleAvailabilityToggle("onSale")}
                  className="w-4 h-4 accent-primary cursor-pointer"
                />
                <span className="text-sm text-section-text">On Sale</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Apply Button (mobile only) */}
      {isMobile && onClose && (
        <div className="p-4 border-t border-section-border bg-section-card-bg">
          <button
            onClick={onClose}
            className="w-full bg-primary text-primary-foreground hover:opacity-90 py-3 rounded-xl font-bold text-base transition-opacity"
          >
            Apply Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductFilterPanel;
